'use client';

// Hooks d'authentification (TanStack Query). La « session » vit dans le cookie
// httpOnly côté serveur ; le client se contente d'interroger /me.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchMe,
  forgotPassword,
  login,
  logout,
  register,
  resendVerification,
  resetPassword,
  verifyEmail,
  type LoginPayload,
  type RegisterPayload,
  type ResetPasswordPayload,
  type VerifyEmailPayload,
} from '@/api/auth';
import type { User } from '@/types/api';

export const meQueryKey = ['me'] as const;

// Utilisateur courant. `data === null` = non authentifié (pas une erreur).
export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LoginPayload) => login(payload),
    onSuccess: (user: User) => {
      qc.setQueryData(meQueryKey, user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      qc.setQueryData(meQueryKey, null);
      void qc.invalidateQueries();
    },
  });
}

export function useRegister() {
  return useMutation({ mutationFn: (payload: RegisterPayload) => register(payload) });
}

export function useVerifyEmail() {
  return useMutation({ mutationFn: (payload: VerifyEmailPayload) => verifyEmail(payload) });
}

export function useResendVerification() {
  return useMutation({ mutationFn: (email: string) => resendVerification(email) });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: (email: string) => forgotPassword(email) });
}

export function useResetPassword() {
  return useMutation({ mutationFn: (payload: ResetPasswordPayload) => resetPassword(payload) });
}
